import * as React from "react";
import { ModalOverlay, Spinner } from "../common";
import {
	CustomFieldContainer,
	ICustomFieldContainerProps,
} from "src/admin/Organization/CustomFields/CustomFieldContainer";
import VitalsButton from "../buttons/VitalsButton";
import { OnRoadButton } from "../buttons/OnRoadButton";
import DropdownInputAnt from "src/components/forms/DropdownInputAnt";
import {
	Assessment,
	IModule,
} from "src/@types/ImpiricaPlatformApi";
import { VitalsSummaryButton } from "../buttons/VitalsSummaryButton";
import NeurapulseButton from "../buttons/NeurapulseButton";
import { useNeurapulse } from "src/hooks/useNeurapulse";
import { toast } from "react-toastify";
import { Utils } from "src/common/utils";
import { NeurapulseLink } from "src/components/neurapulse/NeurapulseLink";

interface IAssessmentProps {
	assessmentResultId: number;
	organizationId: number;
	datasetId: number;
	clientId: number;
	neurapulseEnabled: boolean;
	hasNeurapulseResult: boolean;
	onAssessmentCreated?: (assessment: Assessment) => void;
	onCustomFieldValuesUpdated?: () => void;
}

interface IVitalButtonProps {
	vitalsId?: number;
	vitalsScore?: number;
	dataLocked: boolean;
	licensedProductContainsVitals: boolean;
	DCATRunning: boolean;
	DCATButtonClicked: boolean;
	vitalsLaunched: boolean;
	showLaunchedMessageHandle: boolean;
	canSync: boolean;
	getVitalsTest: () => void;
	resumeVitalsTest: () => void;
	startVitalsTest: () => void;
	canEditVitals: boolean;
}

interface IVitalsSummaryProps {
	careTestResultId?: number;
	dataLocked: boolean;
	canEditCare: boolean;
	getVitalsSummary: (careTestResultId: number | undefined) => void;
}

interface ICoreButtonProps {
	coreTestResultId?: number;
	dataLocked: boolean;
	licensedProductContainsCore: boolean;
	getCoreTest: () => void;
	startCore: () => void;
	canEditCore: boolean;
}

interface IProps {
	assessmentProps?: IAssessmentProps;
	vitalsDateCompleted?: string;
	withAssessment: boolean;
	modalOpen: boolean;
	customFieldProps: ICustomFieldContainerProps;
	vitalButtonProps: IVitalButtonProps;
	vitalsSummaryProps: IVitalsSummaryProps;
	coreButtonProps: ICoreButtonProps;
	customFieldNotValidated: boolean;
	selectedPurposeId?: number;
	productType?: ProductType;
	onSelectedPurposeChange: (value: number) => void;
	closeModal: () => void;
	existingAssessmentResultCloseModal: () => void;
	modules?: IModule[];
}

const ClientResultModalOverlay: React.FunctionComponent<IProps> = ({
	vitalsDateCompleted,
	withAssessment,
	modalOpen,
	customFieldProps,
	vitalButtonProps,
	vitalsSummaryProps,
	coreButtonProps,
	customFieldNotValidated,
	selectedPurposeId,
	productType,
	onSelectedPurposeChange,
	closeModal,
	assessmentProps,
}) => {
	let modal = <React.Fragment />;
	let footer: React.ReactFragment;

	const neurapulseSectionRef = React.useRef<HTMLDivElement>(null);

	const [customFieldValues, setCustomFieldValues] = React.useState<
		CustomFieldValueDTO[]
	>([]);
	const [savingNeurapulse, setSavingNeurapulse] =
		React.useState<boolean>(false);
	const [disableNeurapulse, setDisableNeurapulse] =
		React.useState<boolean>(false);
	const [magicLink, setMagicLink] = React.useState<string>("");

	React.useEffect(() => {
		setMagicLink("");
	}, [modalOpen]);

	const {
		assessmentResultId,
		organizationId,
		datasetId,
		clientId,
		neurapulseEnabled,
		hasNeurapulseResult
	} = assessmentProps || {
		assessmentResultId: 0,
		organizationId: 0,
		datasetId: 0,
		clientId: 0,
		neurapulseEnabled: false,
		hasNeurapulseResult: false,
	};

	const defaultAssessmentPurposes: PickListItemResponseDTO[] = [
		{
			id: 14557,
			description: "Other",
			isActive: true,
			sort: 9,
		},
		{
			id: 14553,
			description: "Periodic",
			isActive: true,
			sort: 2,
		},
		{
			id: 14554,
			description: "Return to work",
			isActive: true,
			sort: 3,
		},
		{
			id: 14555,
			description: "Post-incident",
			isActive: true,
			sort: 4,
		},
		{
			id: 15408,
			description: "Fitness to drive",
			isActive: true,
			sort: 10,
		},
	];

	const commercialAssessmentPurposes: PickListItemResponseDTO[] = [
		...defaultAssessmentPurposes,
		{
			id: 15405,
			description: "Probationary period training",
			isActive: true,
			sort: 6,
		},
		{
			id: 15406,
			description: "Mentoring and training",
			isActive: true,
			sort: 7,
		},
		{
			id: 15407,
			description: "Reasonable cause",
			isActive: true,
			sort: 8,
		},
		{
			id: 14556,
			description: "Performance evaluation",
			isActive: true,
			sort: 5,
		},
		{
			id: 14552,
			description: "Pre-hire evaluation",
			isActive: true,
			sort: 1,
		},
	];

	const sortAssessmentPurposeArrays = (
		purposeArray: PickListItemResponseDTO[]
	) => {
		return purposeArray.sort((a, b) => {
			return a.sort! - b.sort!;
		});
	};

	const onGetCustomFieldValueFromState = (
		customFields: CustomFieldValueDTO[]
	) => {
		setCustomFieldValues(customFields);
		customFieldProps.getCustomFieldValueFromState(customFields);
	};

	const {
		newNeurapulseAssessment,
		addCustomFields,
		updateCustomFields,
		generateLink,
	} = useNeurapulse();

	const onNewNeurapulseAssessment = async (
		clientId: number,
		datasetId: number,
		assessmentPurposeId: number
	) => {
		return await newNeurapulseAssessment(
			organizationId,
			clientId,
			datasetId,
			assessmentPurposeId,
			Utils.mapCustomFieldValues(customFieldValues)
		);
	};

	const onUpdateCustomFields = async () => {
		console.log("Custom Fiekds: ", customFieldValues);
		const customFieldsForUpdate = customFieldValues.filter(
			(cf) => cf.id !== undefined
		);
		const customFieldsForAdd = customFieldValues.filter(
			(cf) => cf.id === undefined
		);
		// Update new Custom Fields For Add with AssessmentResultId
		customFieldsForAdd.forEach((cf) => {
			cf.assessmentResultId = assessmentResultId;
		});

		const updatedFields = await updateCustomFields(
			organizationId,
			datasetId,
			Utils.mapCustomFieldValues(customFieldsForUpdate)
		);
		const addedFields = await addCustomFields(
			organizationId,
			datasetId,
			Utils.mapCustomFieldValues(customFieldsForAdd)
		);

		if (
			assessmentProps &&
			assessmentProps.onCustomFieldValuesUpdated &&
			(updatedFields || addedFields)
		) {
			assessmentProps.onCustomFieldValuesUpdated();
		}
	};

	const neurapulseOnClick = async () => {
		setSavingNeurapulse(true);
		setDisableNeurapulse(true);
		let assessmentId = assessmentResultId;
		let newSessionCreated = false;

		try {
			if (assessmentResultId === 0) {
				if (onNewNeurapulseAssessment) {
					const newAssessment = await onNewNeurapulseAssessment(
						clientId,
						datasetId,
						selectedPurposeId || 0
					);
					if (newAssessment) {
						newSessionCreated = true;
						assessmentId = newAssessment.id;
						if (assessmentProps && assessmentProps.onAssessmentCreated) {
							assessmentProps.onAssessmentCreated(newAssessment);
						}
					}
				}
			} else {
				await onUpdateCustomFields();
			}

			const newMagicLink = await generateLink(
				organizationId,
				datasetId,
				assessmentId
			);
			if (newMagicLink) {
				setMagicLink(newMagicLink);
				if (newSessionCreated) {
					toast.success("Session and link created successfully");
				} else {
					toast.success("Link generated successfully");
				}
			} else {
				toast.error("Error generating link");
			}
		} catch (error) {
			console.error("Error creating session or generating link", error);
			toast.error("Error creating session or generating link");
		} finally {
			setSavingNeurapulse(false);
			setDisableNeurapulse(false);
			scrollToNeurapulse();
		}
	};

	const scrollToNeurapulse = () => {
		if (neurapulseSectionRef.current) {
			neurapulseSectionRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	};

	const renderNeurapulseSection = () => {
		if (magicLink !== "") {
			return (
				<NeurapulseLink
					neurapulseRef={neurapulseSectionRef}
					magicLink={magicLink}
					toast={toast}
				/>
			);
		}
		return null;
	};

	const footerChildrenWithAssessment = () => {
		return (
			<React.Fragment>
				<div className="container">
					<div className="container row">
						{neurapulseEnabled && (
							<NeurapulseButton
								onClick={neurapulseOnClick}
								loading={savingNeurapulse}
								disabled={hasNeurapulseResult || disableNeurapulse || magicLink !== ""}
							/>
						)}
						{!neurapulseEnabled && (
							<VitalsButton
								DCATButtonClicked={vitalButtonProps.DCATButtonClicked}
								vitalsDateCompleted={vitalsDateCompleted}
								DCATRunning={vitalButtonProps.DCATRunning}
								dataLocked={vitalButtonProps.dataLocked}
								licensedProductContainsVitals={
									vitalButtonProps.licensedProductContainsVitals
								}
								vitalsId={vitalButtonProps.vitalsId}
								vitalsScore={vitalButtonProps.vitalsScore}
								resumeVitalsTest={vitalButtonProps.resumeVitalsTest}
								getVitalsTest={vitalButtonProps.getVitalsTest}
								startVitalsTest={vitalButtonProps.startVitalsTest}
								customFieldNotValidated={customFieldNotValidated}
								canSync={vitalButtonProps.canSync}
								canEditVitals={vitalButtonProps.canEditVitals}
							/>
						)}
						<VitalsSummaryButton
							careTestResultId={vitalsSummaryProps.careTestResultId}
							canEditCare={vitalsSummaryProps.canEditCare}
							dataLocked={vitalsSummaryProps.dataLocked}
							getVitalsSummary={vitalsSummaryProps.getVitalsSummary}
							productType={productType}
						/>
						<OnRoadButton
							coreTestResultId={coreButtonProps.coreTestResultId}
							dataLocked={coreButtonProps.dataLocked}
							licensedProductContainsCore={
								coreButtonProps.licensedProductContainsCore
							}
							startCore={coreButtonProps.startCore}
							getCoreTest={coreButtonProps.getCoreTest}
							customFieldNotValidated={customFieldNotValidated}
							canEditCore={coreButtonProps.canEditCore}
							productType={productType}
							selectedPurposeId={selectedPurposeId}
						/>
					</div>
					<div className="d-flex m-0">
						{vitalButtonProps.dataLocked ? (
							<p className="text-danger mr-auto mt-2 mb-0">
								{vitalButtonProps.dataLocked && "VITALS has been locked"}
							</p>
						) : (
							<p className="mr-auto mt-2 mb-0">
								{vitalButtonProps.vitalsLaunched && vitalsLaunchedUX()}
							</p>
						)}
						<p className="text-danger ml-auto mt-2 mb-0">
							{coreButtonProps.dataLocked ? "On-Road has been locked" : ""}
						</p>
					</div>
				</div>
			</React.Fragment>
		);
	};

	const vitalsLaunchedUX = () => {
		if (vitalButtonProps.showLaunchedMessageHandle) {
			return (
				<>
					<span className="text-info">
						All done. We have launched the VITALS application
					</span>
					&nbsp;
					<br />
					<span className="text-warning">
						If the VITALS application hasn't opened, this may not be on an
						authorized VITALS assessment device or an error has occurred
					</span>
				</>
			);
		} else {
			return (
				<span className="text-info mr-auto mt-2 mb-0">
					Launching VITALS application
				</span>
			);
		}
	};

	const footerChildrenWithOutAssessment = () => {
		return (
			<React.Fragment>
				<div className="container">
					<div className="container row">
						{neurapulseEnabled && (
							<NeurapulseButton
								onClick={neurapulseOnClick}
								loading={savingNeurapulse}
								disabled={
									disableNeurapulse ||
									!customFieldNotValidated ||
									selectedPurposeId === undefined
								}
							/>
						)}
						{!neurapulseEnabled && (
							<VitalsButton
								DCATButtonClicked={vitalButtonProps.DCATButtonClicked}
								DCATRunning={vitalButtonProps.DCATRunning}
								dataLocked={vitalButtonProps.dataLocked}
								licensedProductContainsVitals={
									vitalButtonProps.licensedProductContainsVitals
								}
								vitalsDateCompleted={vitalsDateCompleted}
								vitalsId={vitalButtonProps.vitalsId}
								vitalsScore={vitalButtonProps.vitalsScore}
								getVitalsTest={vitalButtonProps.getVitalsTest}
								resumeVitalsTest={vitalButtonProps.resumeVitalsTest}
								startVitalsTest={vitalButtonProps.startVitalsTest}
								customFieldNotValidated={customFieldNotValidated}
								canSync={vitalButtonProps.canSync}
								canEditVitals={vitalButtonProps.canEditVitals}
							/>
						)}
						<OnRoadButton
							dataLocked={coreButtonProps.dataLocked}
							licensedProductContainsCore={
								coreButtonProps.licensedProductContainsCore
							}
							coreTestResultId={coreButtonProps.coreTestResultId}
							getCoreTest={coreButtonProps.getCoreTest}
							startCore={coreButtonProps.startCore}
							customFieldNotValidated={customFieldNotValidated}
							canEditCore={coreButtonProps.canEditCore}
							productType={productType}
							selectedPurposeId={selectedPurposeId}
						/>
					</div>
					<div className="d-flex m-0">
						<p className="mr-auto mt-2 mb-0">
							{vitalButtonProps.vitalsLaunched ? vitalsLaunchedUX() : ""}
						</p>
					</div>
				</div>
			</React.Fragment>
		);
	};

	if (withAssessment) {
		footer = footerChildrenWithAssessment();
	} else {
		footer = footerChildrenWithOutAssessment();
	}
	modal = (
		<ModalOverlay
			isOpen={modalOpen}
			modalSize="modal-lg"
			headerChildren={withAssessment ? "Edit Session" : "New Session"}
			children={
				modalOpen ? (
					<>
						<div className="col-6 mb-2">
							<DropdownInputAnt
								required={true}
								value={selectedPurposeId}
								placeholder="Select a purpose for this session"
								name="AssessmentPurpose"
								pickListCode="AssessmentPurpose"
								specifiedOptions={
									productType === "DriveABLE"
										? sortAssessmentPurposeArrays(defaultAssessmentPurposes)
										: sortAssessmentPurposeArrays(commercialAssessmentPurposes)
								}
								sort={true}
								onChange={(v) => {
									onSelectedPurposeChange(Number(v.key));
								}}
							/>
						</div>
						<div>
							<CustomFieldContainer
								customFieldTypeIds={customFieldProps.customFieldTypeIds}
								customFieldTypes={customFieldProps.customFieldTypes}
								pickListItems={customFieldProps.pickListItems}
								organizationId={customFieldProps.organizationId}
								datasetId={customFieldProps.datasetId}
								getCustomFieldValidation={
									customFieldProps.getCustomFieldValidation
								}
								getCustomFieldValueFromState={onGetCustomFieldValueFromState}
							/>
						</div>
						{renderNeurapulseSection()}
						{footer}
					</>
				) : (
					<Spinner />
				)
			}
			onRequestClose={closeModal}
		/>
	);

	return <React.Fragment>{modal}</React.Fragment>;
};

export default ClientResultModalOverlay;
